import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosCustom } from '../hooks/axiosInstance';

// Get all published blog posts (public)
export const getPublicBlogs = createAsyncThunk(
  'publicBlog/getPublicBlogs',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams ? `/blogs?${queryParams}` : '/blogs';
      const response = await axiosCustom.get(url, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get a single published blog post by ID (public)
export const getPublicBlogById = createAsyncThunk(
  'publicBlog/getPublicBlogById',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosCustom.get(`/blog/${blogId}`, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get a single published blog post by slug (public)
export const getPublicBlogBySlug = createAsyncThunk(
  'publicBlog/getPublicBlogBySlug',
  async (slug, thunkAPI) => {
    try {
      const response = await axiosCustom.get(`/blog/slug/${slug}`, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Increment blog views (public)
export const incrementBlogViews = createAsyncThunk(
  'publicBlog/incrementBlogViews',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosCustom.patch(`/blog/${blogId}/view`, {}, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return { blogId, views: response.data.views };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Increment blog likes (public)
export const incrementBlogLikes = createAsyncThunk(
  'publicBlog/incrementBlogLikes',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosCustom.patch(`/blog/${blogId}/like`, {}, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return { blogId, likes: response.data.likes };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Decrement blog likes (public)
export const decrementBlogLikes = createAsyncThunk(
  'publicBlog/decrementBlogLikes',
  async (blogId, thunkAPI) => {
    try {
      const response = await axiosCustom.patch(`/blog/${blogId}/unlike`, {}, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return { blogId, likes: response.data.likes };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get blog posts by category (public)
export const getBlogsByCategory = createAsyncThunk(
  'publicBlog/getBlogsByCategory',
  async ({ category, params = {} }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams ? `/blogs/category/${category}?${queryParams}` : `/blogs/category/${category}`;
      const response = await axiosCustom.get(url, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get blog posts by tag (public)
export const getBlogsByTag = createAsyncThunk(
  'publicBlog/getBlogsByTag',
  async ({ tag, params = {} }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams ? `/blogs/tag/${tag}?${queryParams}` : `/blogs/tag/${tag}`;
      const response = await axiosCustom.get(url, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get latest blog posts (public)
export const getLatestBlogs = createAsyncThunk(
  'publicBlog/getLatestBlogs',
  async (limit = 5, thunkAPI) => {
    try {
      const response = await axiosCustom.get(`/blogs/latest?limit=${limit}`, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get popular blog posts (public)
export const getPopularBlogs = createAsyncThunk(
  'publicBlog/getPopularBlogs',
  async (limit = 5, thunkAPI) => {
    try {
      const response = await axiosCustom.get(`/blogs/popular?limit=${limit}`, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Clear error action
export const clearPublicBlogError = createAction('publicBlog/clearError');

export const publicBlogSlice = createSlice({
  name: 'publicBlog',
  initialState: {
    // Public blog lists
    publicBlogs: [],
    latestBlogs: [],
    popularBlogs: [],
    categoryBlogs: [],
    tagBlogs: [],
    
    // Current public blog
    currentPublicBlog: null,
    
    // Pagination for public blogs
    publicPagination: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    },
    
    // Loading states
    loading: false,
    loadingLatest: false,
    loadingPopular: false,
    loadingCategory: false,
    loadingTag: false,
    loadingCurrent: false,
    
    // Error state
    error: null,
    
    // Filters for public blog viewing
    publicFilters: {
      category: 'all',
      search: '',
      currentCategory: null,
      currentTag: null
    }
  },
  reducers: {
    setCurrentPublicBlog: (state, action) => {
      state.currentPublicBlog = action.payload;
    },
    clearCurrentPublicBlog: (state) => {
      state.currentPublicBlog = null;
    },
    setPublicFilters: (state, action) => {
      state.publicFilters = { ...state.publicFilters, ...action.payload };
    },
    clearPublicFilters: (state) => {
      state.publicFilters = {
        category: 'all',
        search: '',
        currentCategory: null,
        currentTag: null
      };
    },
    updateBlogViews: (state, action) => {
      const { blogId, views } = action.payload;
      // Update views in current blog if it matches
      if (state.currentPublicBlog && state.currentPublicBlog._id === blogId) {
        state.currentPublicBlog.views = views;
      }
      // Update views in blog lists
      [state.publicBlogs, state.latestBlogs, state.popularBlogs, state.categoryBlogs, state.tagBlogs].forEach(blogList => {
        const blog = blogList.find(b => b._id === blogId);
        if (blog) {
          blog.views = views;
        }
      });
    },
    updateBlogLikes: (state, action) => {
      const { blogId, likes } = action.payload;
      // Update likes in current blog if it matches
      if (state.currentPublicBlog && state.currentPublicBlog._id === blogId) {
        state.currentPublicBlog.likes = likes;
      }
      // Update likes in blog lists
      [state.publicBlogs, state.latestBlogs, state.popularBlogs, state.categoryBlogs, state.tagBlogs].forEach(blogList => {
        const blog = blogList.find(b => b._id === blogId);
        if (blog) {
          blog.likes = likes;
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Public Blogs
      .addCase(getPublicBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicBlogs.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.blogs) {
          state.publicBlogs = action.payload.blogs;
          state.publicPagination = {
            currentPage: action.payload.currentPage || 1,
            totalPages: action.payload.totalPages || 1,
            total: action.payload.total || action.payload.blogs.length
          };
        } else {
          state.publicBlogs = action.payload;
          state.publicPagination = {
            currentPage: 1,
            totalPages: 1,
            total: action.payload.length
          };
        }
      })
      .addCase(getPublicBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Public Blog By ID
      .addCase(getPublicBlogById.pending, (state) => {
        state.loadingCurrent = true;
        state.error = null;
      })
      .addCase(getPublicBlogById.fulfilled, (state, action) => {
        state.loadingCurrent = false;
        state.currentPublicBlog = action.payload;
      })
      .addCase(getPublicBlogById.rejected, (state, action) => {
        state.loadingCurrent = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Public Blog By Slug
      .addCase(getPublicBlogBySlug.pending, (state) => {
        state.loadingCurrent = true;
        state.error = null;
      })
      .addCase(getPublicBlogBySlug.fulfilled, (state, action) => {
        state.loadingCurrent = false;
        state.currentPublicBlog = action.payload;
      })
      .addCase(getPublicBlogBySlug.rejected, (state, action) => {
        state.loadingCurrent = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Increment Blog Views
      .addCase(incrementBlogViews.fulfilled, (state, action) => {
        const { blogId, views } = action.payload;
        // Update views across all relevant state
        if (state.currentPublicBlog && state.currentPublicBlog._id === blogId) {
          state.currentPublicBlog.views = views;
        }
        // Update in blog lists
        [state.publicBlogs, state.latestBlogs, state.popularBlogs, state.categoryBlogs, state.tagBlogs].forEach(blogList => {
          const blog = blogList.find(b => b._id === blogId);
          if (blog) {
            blog.views = views;
          }
        });
      })

      // Increment Blog Likes
      .addCase(incrementBlogLikes.fulfilled, (state, action) => {
        const { blogId, likes } = action.payload;
        // Update likes across all relevant state
        if (state.currentPublicBlog && state.currentPublicBlog._id === blogId) {
          state.currentPublicBlog.likes = likes;
        }
        // Update in blog lists
        [state.publicBlogs, state.latestBlogs, state.popularBlogs, state.categoryBlogs, state.tagBlogs].forEach(blogList => {
          const blog = blogList.find(b => b._id === blogId);
          if (blog) {
            blog.likes = likes;
          }
        });
      })

      // Decrement Blog Likes
      .addCase(decrementBlogLikes.fulfilled, (state, action) => {
        const { blogId, likes } = action.payload;
        // Update likes across all relevant state
        if (state.currentPublicBlog && state.currentPublicBlog._id === blogId) {
          state.currentPublicBlog.likes = likes;
        }
        // Update in blog lists
        [state.publicBlogs, state.latestBlogs, state.popularBlogs, state.categoryBlogs, state.tagBlogs].forEach(blogList => {
          const blog = blogList.find(b => b._id === blogId);
          if (blog) {
            blog.likes = likes;
          }
        });
      })

      // Get Blogs By Category
      .addCase(getBlogsByCategory.pending, (state) => {
        state.loadingCategory = true;
        state.error = null;
      })
      .addCase(getBlogsByCategory.fulfilled, (state, action) => {
        state.loadingCategory = false;
        state.categoryBlogs = action.payload.blogs || action.payload;
        state.publicFilters.currentCategory = action.payload.category;
        if (action.payload.blogs) {
          state.publicPagination = {
            currentPage: action.payload.currentPage || 1,
            totalPages: action.payload.totalPages || 1,
            total: action.payload.total || action.payload.blogs.length
          };
        }
      })
      .addCase(getBlogsByCategory.rejected, (state, action) => {
        state.loadingCategory = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Blogs By Tag
      .addCase(getBlogsByTag.pending, (state) => {
        state.loadingTag = true;
        state.error = null;
      })
      .addCase(getBlogsByTag.fulfilled, (state, action) => {
        state.loadingTag = false;
        state.tagBlogs = action.payload.blogs || action.payload;
        state.publicFilters.currentTag = action.payload.tag;
        if (action.payload.blogs) {
          state.publicPagination = {
            currentPage: action.payload.currentPage || 1,
            totalPages: action.payload.totalPages || 1,
            total: action.payload.total || action.payload.blogs.length
          };
        }
      })
      .addCase(getBlogsByTag.rejected, (state, action) => {
        state.loadingTag = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Latest Blogs
      .addCase(getLatestBlogs.pending, (state) => {
        state.loadingLatest = true;
        state.error = null;
      })
      .addCase(getLatestBlogs.fulfilled, (state, action) => {
        state.loadingLatest = false;
        state.latestBlogs = action.payload;
      })
      .addCase(getLatestBlogs.rejected, (state, action) => {
        state.loadingLatest = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Popular Blogs
      .addCase(getPopularBlogs.pending, (state) => {
        state.loadingPopular = true;
        state.error = null;
      })
      .addCase(getPopularBlogs.fulfilled, (state, action) => {
        state.loadingPopular = false;
        state.popularBlogs = action.payload;
      })
      .addCase(getPopularBlogs.rejected, (state, action) => {
        state.loadingPopular = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Clear Error
      .addCase(clearPublicBlogError, (state) => {
        state.error = null;
      });
  },
});

export const { 
  setCurrentPublicBlog, 
  clearCurrentPublicBlog, 
  setPublicFilters, 
  clearPublicFilters,
  updateBlogViews,
  updateBlogLikes 
} = publicBlogSlice.actions;

export default publicBlogSlice.reducer;